import React, { useState } from "react";
import { Filter, ArrowUpDown, X, Calendar, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import moment from "moment";
import toast from "react-hot-toast"; // Assuming react-hot-toast is available

export default function TopHeader({ onSort, reload, sortBy, isDollar }) { // Added sortBy, isDollar props
  const [showFilter, setShowFilter] = useState(false);
  const [showSort, setShowSort] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [customRange, setCustomRange] = useState({ start: "", end: "" });
  const [activeFilter, setActiveFilter] = useState("This Week"); // State to track selected filter

  const filters = [
    "Today",
    "This Week",
    "Last Week",
    "This Month",
    "Last Month",
    "Last 3 Months",
    "Last Year",
    "Custom",
  ];

  const sortOptions = [
    { label: "Latest First (Default)", value: "time_desc" },
    { label: "Oldest First", value: "time_asc" },
    { label: "Side: Buy → Sell", value: "buy_sell" },
    { label: "Side: Sell → Buy", value: "sell_buy" },
    { label: "Symbol A–Z", value: "symbol_asc" },
    { label: "Symbol Z–A", value: "symbol_desc" },
  ];

  // --- Handlers ---
  const handleFilterSelect = (item) => {
    let start, end;
    
    // 1. Set the active filter label
    setActiveFilter(item);

    switch (item) {
      case "Today":
        start = moment().startOf("day");
        end = moment().endOf("day");
        break;
      case "This Week":
        start = moment().startOf("week");
        end = moment().endOf("week");
        break;
      case "Last Week":
        start = moment().subtract(1, "week").startOf("week");
        end = moment().subtract(1, "week").endOf("week");
        break;
      case "This Month":
        start = moment().startOf("month");
        end = moment().endOf("month");
        break;
      case "Last Month":
        start = moment().subtract(1, "month").startOf("month");
        end = moment().subtract(1, "month").endOf("month");
        break;
      case "Last 3 Months":
        // Correctly calculates the range from 3 months ago up to EOD today
        start = moment().subtract(3, "months").startOf("day");
        end = moment().endOf("day");
        break;
      case "Last Year":
        // Start one year ago from today, End is today
        start = moment().subtract(1, "year").startOf("day");
        end = moment().endOf("day");
        break;
      case "Custom":
        setShowCustom(true);
        setShowFilter(false);
        return;
      default:
        return;
    }

    // 2. Call reload function
    reload(start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD"));
    setShowFilter(false);
  };

  const handleCustomSubmit = () => {
    if (!customRange.start || !customRange.end) {
      toast.error("Please select both start and end dates.");
      return;
    }
    
    setActiveFilter(`Custom: ${customRange.start} to ${customRange.end}`);
    reload(customRange.start, customRange.end);
    setShowCustom(false);
    setShowFilter(false);
  };
  
  const handleSortSelect = (value) => {
    onSort?.(value);
    setShowSort(false);
  };

  // --- Rendering ---
  return (
    <>
      {/* 🔝 Header Bar */}
      <div className="flex justify-between items-center px-4 py-3 border-b-2 border-[var(--hover)] bg-[var(--background)]">
        <button
          onClick={() => setShowSort(true)}
          className="p-2 rounded-full bg-[var(--hover)]/30 transition "
        >
          <ArrowUpDown className="text-[22px] text-[var(--text)]" />
        </button>

        <div className="text-[var(--text)] font-semibold text-base select-none flex flex-col items-center">
            Pending Orders
            <span className="text-[10px] text-[var(--subtext)] font-medium opacity-80 mt-[2px]">
                {activeFilter}
            </span>
        </div>

        <button
          onClick={() => setShowFilter(true)}
          className="p-2 rounded-full bg-[var(--hover)]/30 transition hover:bg-[var(--hover)]"
        >
          <Filter className="text-[22px] text-[var(--text)]" />
        </button>
      </div>

      {/* 🧭 Filter Modal (Z-INDEX: 50) */}
      <AnimatePresence>
        {showFilter && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-45"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowFilter(false)}
            />
            <motion.div
              className="fixed bottom-0 left-0 right-0 z-50 border-t-2 border-t-[var(--hover)] bg-[var(--background)] rounded-t-3xl shadow-xl p-6 max-w-md mx-auto"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ duration: 0.25, ease: "easeOut" }}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold text-[var(--text)] flex items-center gap-2">
                  <Filter className="w-5 h-5 text-[var(--primary)]" /> Filter by Date
                </h2>
                <button
                  onClick={() => setShowFilter(false)}
                  className="p-2 rounded-full hover:bg-[var(--hover)]"
                >
                  <X className="w-5 h-5 text-[var(--subtext)]" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {filters.map((item) => (
                  <button
                    key={item}
                    onClick={() => handleFilterSelect(item)}
                    className={`
                        px-3 py-2 text-sm rounded-lg border-2 transition shadow-sm font-medium flex justify-center items-center gap-2
                        ${item === activeFilter ? 
                            "border-[var(--primary)] bg-[var(--primary)] text-white" : 
                            "border-[var(--hover)] bg-[var(--background)] text-[var(--text)] hover:border-[var(--primary)]"
                        }`}
                  >
                    {item}
                    {item === activeFilter && <Check className="w-4 h-4" />}
                  </button>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* 🧩 Sort Modal (Z-INDEX: 50) */}
      <AnimatePresence>
        {showSort && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-45"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowSort(false)}
            />
            <motion.div
              className="fixed bottom-0 left-0 right-0 border-t-2 border-t-[var(--hover)] z-50 bg-[var(--background)] rounded-t-3xl shadow-lg p-6 max-w-md mx-auto"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ duration: 0.2 }}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold text-[var(--text)] flex items-center gap-2">
                    <ArrowUpDown className="w-5 h-5 text-[var(--primary)]" /> Sort Orders
                </h2>
                <button
                  onClick={() => setShowSort(false)}
                  className="p-2 rounded-full hover:bg-[var(--hover)]"
                >
                  <X className="w-5 h-5 text-[var(--subtext)]" />
                </button>
              </div>

              <div className="space-y-2">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => handleSortSelect(opt.value)}
                    className={`
                        w-full px-3 py-3 text-sm rounded-lg border-2 transition shadow-sm text-left font-medium flex justify-between items-center
                        ${opt.value === sortBy ? 
                            "border-[var(--primary)] bg-[var(--primary)] text-white" : 
                            "border-[var(--hover)] bg-[var(--background)] text-[var(--text)] hover:border-[var(--primary)]"
                        }`}
                  >
                    {opt.label}
                    {opt.value === sortBy && <Check className="w-4 h-4" />}
                  </button>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* 📅 Custom Range Modal (Z-INDEX: 55) */}
      <AnimatePresence>
        {showCustom && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/50 backdrop-blur z-54" // Higher backdrop z-index
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowCustom(false)}
            />
            <motion.div
              className="fixed bottom-0 left-0 pb-9 right-0 z-55 bg-[var(--background)] rounded-t-3xl shadow-lg p-6 max-w-md mx-auto border-t-2 border-t-[var(--primary)]"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ duration: 0.2 }}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold text-[var(--text)] flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[var(--primary)]" /> Custom Range
                </h2>
                <button
                  onClick={() => setShowCustom(false)}
                  className="p-2 rounded-full hover:bg-[var(--hover)]"
                >
                  <X className="w-5 h-5 text-[var(--subtext)]" />
                </button>
              </div>

              <div className="space-y-4 mb-9 pb-9">
                <div>
                  <label className="text-xs text-[var(--subtext)] mb-1 block font-medium">
                    Start Date
                  </label>
                  <input
                    type="date"
                    max={moment().format("YYYY-MM-DD")}
                    value={customRange.start}
                    onChange={(e) =>
                      setCustomRange({ ...customRange, start: e.target.value })
                    }
                    className="w-full px-4 py-3 border-2 rounded-lg bg-transparent text-[var(--text)] border-[var(--hover)] focus:ring-2 focus:ring-[var(--primary)] focus:border-[var(--primary)] outline-none transition"
                  />
                </div>

                <div>
                  <label className="text-xs text-[var(--subtext)] mb-1 block font-medium">
                    End Date
                  </label>
                  <input
                    type="date"
                    max={moment().format("YYYY-MM-DD")}
                    value={customRange.end}
                    onChange={(e) =>
                      setCustomRange({ ...customRange, end: e.target.value })
                    }
                    className="w-full px-4 py-3 border-2 rounded-lg bg-transparent text-[var(--text)] border-[var(--hover)] focus:ring-2 focus:ring-[var(--primary)] focus:border-[var(--primary)] outline-none transition"
                  />
                </div>

                <button
                  onClick={handleCustomSubmit}
                  disabled={!customRange.start || !customRange.end}
                  className="w-full bg-[var(--primary)] text-white py-3 rounded-lg font-bold text-base transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Apply Filter
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}